import React, { useState, useEffect } from 'react';
import {
  Briefcase, Buildings, Crane, Door, DoorOpen, GlobeHemisphereWest, HouseLine, Key, MapPin, Plus, Storefront, User, Users, UsersThree,
  type Icon,
} from '@phosphor-icons/react';
import { supabase } from '../../../lib/supabase/client';
import { eigentuemerAnzeigename, istFirma, ansprechpartnerZusatz } from '../../../lib/eigentuemer/anzeige';
import { FormRahmen } from '../../../components/island/form/FormRahmen';
import { FormAbschnitt } from '../../../components/island/form/FormAbschnitt';
import { FormFeld, FormZeile } from '../../../components/island/form/FormFeld';
import { FormAuswahl, type FormAuswahlOption } from '../../../components/island/form/FormAuswahl';
import { FormKacheln, type FormKachelOption } from '../../../components/island/form/FormKacheln';
import { FormSuche } from '../../../components/island/form/FormSuche';
import { FormKnopfleiste } from '../../../components/island/form/FormKnopfleiste';
import { useFormFehler } from '../../../components/island/form/useFormFehler';
import { ABSCHNITT_FARBE, ZEILEN_ICON } from '../../../components/island/form/form-stil';

interface Eigentuemer {
  id: string;
  anrede?: string | null;
  vorname: string | null;
  nachname: string | null;
  firma_name?: string | null;
}

interface TeamMember {
  id: string;
  full_name: string;
}

interface Team {
  id: string;
  name: string;
}

// Optionen exakt wie bisher im <select>; Icons je Objekttyp aus dem Prototyp (Handoff Pillen-Formulare 01).
const OBJEKTTYPEN: readonly { wert: string; icon: Icon }[] = [
  { wert: 'Mehrfamilienhaus', icon: Buildings },
  { wert: 'Einfamilienhaus', icon: HouseLine },
  { wert: 'Mietverwaltung', icon: Key },
  { wert: 'WEG', icon: UsersThree },
  { wert: 'Sondereigentum', icon: Door },
  { wert: 'Teileigentum', icon: Storefront },
  { wert: 'Gewerbe', icon: Briefcase },
  { wert: 'Neubau', icon: Crane },
];

type Verwaltungsart = 'WEG' | 'MV' | 'GV' | 'SEV';
// Texte wie bisher („WEG — Wohnungseigentum“), als Kürzel + Bezeichnung auf die Kachel verteilt.
const VERWALTUNGSARTEN: readonly FormKachelOption<Verwaltungsart>[] = [
  { wert: 'WEG', kuerzel: 'WEG', label: 'Wohnungseigentum', icon: UsersThree, verlauf: ABSCHNITT_FARBE.blau.verlauf, tip: 'WEG — Wohnungseigentum' },
  { wert: 'MV', kuerzel: 'MV', label: 'Mietverwaltung', icon: Key, verlauf: ABSCHNITT_FARBE.gruen.verlauf, tip: 'MV — Mietverwaltung' },
  { wert: 'GV', kuerzel: 'GV', label: 'Gewerbeverwaltung', icon: Storefront, verlauf: ABSCHNITT_FARBE.orange.verlauf, tip: 'GV — Gewerbeverwaltung' },
  { wert: 'SEV', kuerzel: 'SEV', label: 'Sondereigentum', icon: DoorOpen, verlauf: ABSCHNITT_FARBE.violett.verlauf, tip: 'SEV — Sondereigentum' },
];

const PFLICHT_FELDER = ['objektnummer', 'strasse', 'plz', 'stadt', 'verwaltungsart'] as const;

export default function NeuesObjektFormular({ onDone }: { onDone: () => void }) {
  const [loading, setLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [eigentuemerList, setEigentuemerList] = useState<Eigentuemer[]>([]);
  const [isOwnerOpen, setIsOwnerOpen] = useState(false);
  const [ownerSearchQuery, setOwnerSearchQuery] = useState('');
  const fehler = useFormFehler();

  const [formData, setFormData] = useState({
    objektnummer: '',
    bezeichnung: '',
    strasse: '',
    hausnummer: '',
    plz: '',
    stadt: '',
    objekttyp: 'Mehrfamilienhaus',
    verwaltungsart: '',
    zustaendig: 'Global',
    zustaendig_id: null as string | null,
    zustaendig_typ: null as 'mitarbeiter' | 'team' | null,
    eigentuemer: '',
    eigentuemer_id: null as string | null,
    status: 'Aktiv',
  });

  useEffect(() => {
    fetchTeamMembers();
    fetchTeams();
    fetchEigentuemer();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .or('role.eq.team,role.eq.admin').eq('zuweisbar', true)
        .order('full_name');
      if (error) throw error;
      setTeamMembers(data || []);
    } catch (err) {
      console.error('Error fetching team members:', err);
    }
  };

  const fetchTeams = async () => {
    try {
      const { data, error } = await supabase
        .from('teams')
        .select('id, name')
        .order('name');
      if (error) throw error;
      setTeams(data || []);
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  const fetchEigentuemer = async () => {
    try {
      const { data, error } = await supabase
        .from('eigentuemer')
        .select('id, anrede, vorname, nachname, firma_name')
        .order('nachname', { ascending: true });
      if (error) throw error;
      setEigentuemerList(data || []);
    } catch (err) {
      console.error('Error fetching eigentuemer:', err);
    }
  };

  const setzeFeld = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    fehler.korrigiert(name);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setzeFeld(e.target.name, e.target.value);
  };

  const selectAssignee = (ziel: { id: string; name: string; typ: 'mitarbeiter' | 'team' } | null) => {
    setFormData(prev => ({
      ...prev,
      zustaendig: ziel ? ziel.name : 'Global',
      zustaendig_id: ziel ? ziel.id : null,
      zustaendig_typ: ziel ? ziel.typ : null,
    }));
  };

  const setzeEigentuemerSuche = (val: string) => {
    setOwnerSearchQuery(val);
    setFormData(prev => ({ ...prev, eigentuemer: val, eigentuemer_id: null }));
    setIsOwnerOpen(true);
  };

  const selectOwner = (owner: Eigentuemer) => {
    const fullName = eigentuemerAnzeigename(owner);
    setOwnerSearchQuery(fullName);
    setFormData(prev => ({ ...prev, eigentuemer: fullName, eigentuemer_id: owner.id }));
    setIsOwnerOpen(false);
  };

  const filteredEigentuemer = eigentuemerList.filter(e => {
    const suchtext = `${eigentuemerAnzeigename(e)} ${e.firma_name || ''}`.toLowerCase();
    return suchtext.includes(ownerSearchQuery.toLowerCase());
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fehlend = PFLICHT_FELDER.filter(k => !formData[k].trim());
    if (fehlend.length) {
      fehler.zeige('Bitte Pflichtfelder ausfüllen: Objektnummer, Straße, PLZ, Stadt, Verwaltungsart.', fehlend);
      return;
    }
    fehler.leeren();
    setLoading(true);
    try {
      const { data: inserted, error } = await supabase
        .from('objekte')
        .insert([{ ...formData }])
        .select('id')
        .single();
      if (error) throw error;
      if (inserted?.id) {
        supabase.functions
          .invoke('gdrive', { body: { action: 'provision-objekt', objektId: inserted.id } })
          .catch((e) => console.warn('Ordner-Provisionierung nachgelagert (Cron übernimmt):', e));
      }
      onDone();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unbekannter Datenbankfehler';
      fehler.zeige('Fehler beim Speichern: ' + msg);
    } finally {
      setLoading(false);
    }
  };

  // Zuständigkeit: Global, Mitarbeiter, Teams – Schlüssel trennt die beiden Arten.
  const zustaendigOptionen: FormAuswahlOption<string>[] = [
    { wert: 'global', label: 'Global', icon: GlobeHemisphereWest, iconFlaeche: ZEILEN_ICON.blau.flaeche, iconFarbe: ZEILEN_ICON.blau.farbe },
    ...teamMembers.map(m => ({ wert: `m:${m.id}`, label: m.full_name, icon: User, iconFlaeche: ZEILEN_ICON.gruen.flaeche, iconFarbe: ZEILEN_ICON.gruen.farbe })),
    ...teams.map(t => ({ wert: `t:${t.id}`, label: t.name, icon: Users, iconFlaeche: ZEILEN_ICON.violett.flaeche, iconFarbe: ZEILEN_ICON.violett.farbe })),
  ];
  const zustaendigWert = formData.zustaendig_typ === 'mitarbeiter' ? `m:${formData.zustaendig_id}`
    : formData.zustaendig_typ === 'team' ? `t:${formData.zustaendig_id}` : 'global';
  const waehleZustaendig = (wert: string) => {
    if (wert === 'global') return selectAssignee(null);
    const m = teamMembers.find(x => `m:${x.id}` === wert);
    if (m) return selectAssignee({ id: m.id, name: m.full_name, typ: 'mitarbeiter' });
    const t = teams.find(x => `t:${x.id}` === wert);
    if (t) selectAssignee({ id: t.id, name: t.name, typ: 'team' });
  };

  const fehltIn = (felder: readonly string[]) => felder.filter(f => fehler.istFehler(f)).length;

  return (
    <FormRahmen onSubmit={handleSubmit} fehler={fehler.text}
      knopfleiste={<FormKnopfleiste haupt={{ label: 'Objekt anlegen', icon: Plus, laedt: loading }} />}>
      <FormAbschnitt titel="Objekt" icon={Buildings} farbe="blau" pflicht={1} fehlt={fehltIn(['objektnummer'])} index={0}
        tip="Objekt – Interne Objektnummer und optionale Bezeichnung">
        <FormZeile>
          <FormFeld label="Objektnummer" name="objektnummer" value={formData.objektnummer} onChange={handleChange}
            placeholder="z.B. OBJ-001" pflicht fehler={fehler.istFehler('objektnummer')} />
          <FormFeld label="Bezeichnung" name="bezeichnung" value={formData.bezeichnung} onChange={handleChange}
            placeholder="z.B. Residenz am Park" />
        </FormZeile>
      </FormAbschnitt>

      <FormAbschnitt titel="Adresse" icon={MapPin} farbe="rot" pflicht={3} fehlt={fehltIn(['strasse', 'plz', 'stadt'])} index={1}
        tip="Adresse – Wird für Karte, Briefe und E-Mail-Zuordnung genutzt">
        <FormZeile spalten="minmax(0,2fr) minmax(0,1fr)">
          <FormFeld label="Straße" name="strasse" value={formData.strasse} onChange={handleChange}
            placeholder="Musterstraße" pflicht fehler={fehler.istFehler('strasse')} />
          <FormFeld label="Nr." name="hausnummer" value={formData.hausnummer} onChange={handleChange} placeholder="12" />
        </FormZeile>
        <FormZeile>
          <FormFeld label="PLZ" name="plz" value={formData.plz} onChange={handleChange}
            placeholder="35037" pflicht fehler={fehler.istFehler('plz')} />
          <FormFeld label="Stadt" name="stadt" value={formData.stadt} onChange={handleChange}
            placeholder="Marburg" pflicht fehler={fehler.istFehler('stadt')} />
        </FormZeile>
      </FormAbschnitt>

      <FormAbschnitt titel="Verwaltung" icon={Briefcase} farbe="gold" pflicht={1} fehlt={fehltIn(['verwaltungsart'])} index={2}
        tip="Verwaltung – Legt fest, wer das Objekt betreut und wem es gehört">
        <FormAuswahl label="Objekttyp" tip="Objekttyp – Art des Gebäudes"
          optionen={OBJEKTTYPEN.map(t => ({ wert: t.wert, label: t.wert, icon: t.icon }))}
          wert={formData.objekttyp} onChange={(v) => setzeFeld('objekttyp', v)} />
        <FormKacheln label="Verwaltungsart" pflicht fehler={fehler.istFehler('verwaltungsart')}
          tip="Verwaltungsart – Bestimmt, welche Funktionen für das Objekt gelten"
          optionen={VERWALTUNGSARTEN} wert={formData.verwaltungsart as Verwaltungsart | ''}
          onChange={(v) => setzeFeld('verwaltungsart', v)} />
        <FormAuswahl label="Zuständigkeit" tip="Zuständigkeit – Wer das Objekt betreut"
          optionen={zustaendigOptionen} wert={zustaendigWert} anzeige={formData.zustaendig} onChange={waehleZustaendig} />
        <FormSuche label="Eigentümer" platzhalter="Name suchen..." tip="Eigentümer – Nach Name oder Firma suchen"
          wert={ownerSearchQuery} onChange={(e) => setzeEigentuemerSuche(e.target.value)}
          offen={isOwnerOpen} mindestLaenge={1}
          onFocus={() => setIsOwnerOpen(true)}
          onBlur={() => setTimeout(() => setIsOwnerOpen(false), 150)}
          onSchliessen={() => setIsOwnerOpen(false)}
          ausgewaehlt={!!formData.eigentuemer_id} onEntfernen={() => setzeEigentuemerSuche('')}
          treffer={filteredEigentuemer.map(e => ({
            id: e.id,
            label: eigentuemerAnzeigename(e),
            unter: ansprechpartnerZusatz(e) || undefined,
            icon: istFirma(e) ? Buildings : User,
            iconFlaeche: ZEILEN_ICON.gedaempft.flaeche,
            iconFarbe: ZEILEN_ICON.gedaempft.farbe,
          }))}
          onWaehlen={(id) => { const e = eigentuemerList.find(x => x.id === id); if (e) selectOwner(e); }} />
      </FormAbschnitt>
    </FormRahmen>
  );
}
